import React, { useState, useMemo } from 'react';
import { calculateIkrProgress } from '../../lib/utils';
import ProgressDonut from '../../components/ui/ProgressDonut';
import SelfEvaluationModal from './SelfEvaluationModal';
import ManagerFeedbackModal from './ManagerFeedbackModal';

export default function EvaluationRow({ ikr, evaluation, viewer, period, employeeStatus, onSelfEvalSubmit, onManagerFeedbackSubmit, managers, evaluationMode, selectedPerson, users, rowIndex = 0 }) {
  const [showSelfEvalModal, setShowSelfEvalModal] = useState(false);
  const [showManagerFeedbackModal, setShowManagerFeedbackModal] = useState(false);

  const selectedPersonDoc = useMemo(() => {
    if (!selectedPerson || !Array.isArray(users)) return null;
    return users.find(u => (u.uid || u.id) === selectedPerson);
  }, [selectedPerson, users]);

  const isMappedManager = useMemo(() => {
    if (!selectedPersonDoc || !viewer) return false;
    const viewerId = viewer.uid || viewer.id;
    const viewerEmail = viewer.email?.toLowerCase().trim();
    const managerId = selectedPersonDoc.managerId;
    const managerEmail = selectedPersonDoc.managerEmail?.toLowerCase().trim();
    return managerId === viewerId || managerEmail === viewerEmail;
  }, [selectedPersonDoc, viewer]);

  const canEditSelf = evaluationMode === 'employee' && viewer.uid === ikr.ownerId && employeeStatus === 'pending';
  const canEditManager = evaluationMode === 'manager' && isMappedManager && employeeStatus === 'manager-review' && viewer.uid !== selectedPerson;
  const canViewResponses = viewer.uid === ikr.ownerId || evaluationMode === 'admin' || (evaluationMode === 'manager' && isMappedManager);
  const isAdminMode = evaluationMode === 'admin';

  const ikrForProgress = {
    quarterlyActuals: ikr.quarterlyActuals || [ikr.q1Actual, ikr.q2Actual, ikr.q3Actual, ikr.q4Actual],
    kpiTarget: ikr.targetKpi || ikr.kpiTarget,
    calculationMethod: ikr.calculationMethod || 'sum'
  };
  // `trackingProgress` is computed from the raw OKR doc (owner-thread aware);
  // fall back to the flat fields for anything that doesn't supply it.
  const progressDecimal = ikr.trackingProgress ?? calculateIkrProgress(ikrForProgress);
  const normalizedProgressDecimal = isNaN(progressDecimal) ? 0 : Math.min(Math.max(progressDecimal, 0), 1);
  const progress = normalizedProgressDecimal * 100;
  const hasSelfEval = evaluation?.selfEvaluation?.wentWell || evaluation?.selfEvaluation?.couldBeBetter || evaluation?.selfEvaluation?.nextFocus;
  const hasManagerFeedback = evaluation?.managerFeedback?.feedback;

  return (
    <>
      <tr className={`border-b border-gray-100 hover:bg-gray-100/80 transition-colors ${rowIndex % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'}`}>
        <td className="px-4 py-3">
          {/* Where this IKR sits in the OKR tracking tree */}
          {(ikr.objectiveName || ikr.krCode) && (
            <div className="mb-1.5">
              {ikr.objectiveName && (
                <div className="flex items-start gap-1.5">
                  {ikr.objectiveCode && (
                    <span className="text-[10px] font-mono font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded flex-shrink-0">
                      {ikr.objectiveCode}
                    </span>
                  )}
                  <span className="text-xs text-gray-600 leading-snug">{ikr.objectiveText || ikr.objectiveName}</span>
                </div>
              )}
              {ikr.krCode && (
                <div className="flex items-center flex-wrap gap-1.5 mt-1">
                  <span className="text-[10px] font-mono font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                    {ikr.krCode}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="font-medium text-gray-900">{ikr.krName || <span className="italic text-gray-400">Untitled IKR</span>}</div>
          <div className="text-sm text-gray-500">Target: {ikr.targetKpi ?? '—'} {ikr.kpiUnit}</div>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm font-medium text-gray-900">
            {period === 'Q1' && (ikr.q1Actual ?? ikr.quarterlyActuals?.[0] ?? '')}
            {period === 'Q2' && (ikr.q2Actual ?? ikr.quarterlyActuals?.[1] ?? '')}
            {period === 'Q3' && (ikr.q3Actual ?? ikr.quarterlyActuals?.[2] ?? '')}
            {period === 'Q4' && (ikr.q4Actual ?? ikr.quarterlyActuals?.[3] ?? '')}
            {period === 'Annual' && ((ikr.q1Actual ?? ikr.quarterlyActuals?.[0] ?? 0) + (ikr.q2Actual ?? ikr.quarterlyActuals?.[1] ?? 0) + (ikr.q3Actual ?? ikr.quarterlyActuals?.[2] ?? 0) + (ikr.q4Actual ?? ikr.quarterlyActuals?.[3] ?? 0))}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center">
            <ProgressDonut progress={normalizedProgressDecimal} size={32} />
            <span className="ml-2 text-sm font-semibold text-gray-700">{progress.toFixed(1)}%</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="space-y-2">
            {canEditSelf && !hasSelfEval && (
              <button onClick={() => setShowSelfEvalModal(true)} className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">Self-Evaluation</button>
            )}
            {hasSelfEval && canViewResponses && (
              <button onClick={() => setShowSelfEvalModal(true)} className={`px-3 py-2 text-sm rounded-md ${canEditSelf && !isAdminMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-400 text-white hover:bg-gray-500 cursor-default'}`}>
                {canEditSelf && !isAdminMode ? 'Edit Response' : 'View Response'}
              </button>
            )}
            {/* One message, not two: these conditions used to overlap and render
                "Not submittedStage completed - cannot edit" side by side. */}
            {!canEditSelf && !hasSelfEval && !isAdminMode && (
              employeeStatus === 'pending'
                ? <span className="text-sm text-gray-500">Not submitted</span>
                : <span className="text-sm text-red-500">Not submitted — stage moved on, ask an admin to reopen Self-Review</span>
            )}
            {isAdminMode && !hasSelfEval && <span className="text-sm text-gray-400 italic">Not submitted</span>}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="space-y-2">
            {canEditManager && !hasManagerFeedback && (
              <button onClick={() => setShowManagerFeedbackModal(true)} className="px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700">Manager Feedback</button>
            )}
            {hasManagerFeedback && canViewResponses && (
              <button onClick={() => setShowManagerFeedbackModal(true)} className={`px-3 py-2 text-sm rounded-md ${canEditManager && !isAdminMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-400 text-white hover:bg-gray-500 cursor-default'}`}>
                {canEditManager && !isAdminMode ? 'Edit Response' : 'View Response'}
              </button>
            )}
            {!canEditManager && !hasManagerFeedback && !isAdminMode && (
              <span className="text-sm text-gray-500">
                {employeeStatus === 'pending' ? 'Complete self-evaluation first' : employeeStatus === 'manager-review' ? 'Waiting for manager feedback' : 'Not submitted'}
              </span>
            )}
            {isAdminMode && !hasManagerFeedback && <span className="text-sm text-gray-400 italic">Not submitted</span>}
          </div>
        </td>
        <td className="px-4 py-3">
          {hasManagerFeedback && evaluation?.managerFeedback?.rating && (
            <div className="text-sm">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                evaluation.managerFeedback.rating === 'exceeds' ? 'bg-green-100 text-green-800' :
                evaluation.managerFeedback.rating === 'meets' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
              }`}>
                {evaluation.managerFeedback.rating === 'exceeds' ? 'Exceeds' : evaluation.managerFeedback.rating === 'meets' ? 'Meets' : 'Needs Improvement'}
              </span>
            </div>
          )}
        </td>
      </tr>
      <SelfEvaluationModal isOpen={showSelfEvalModal} onClose={() => setShowSelfEvalModal(false)} ikr={ikr} onSubmit={(data) => onSelfEvalSubmit(ikr.id, data)} existingEvaluation={evaluation} canEdit={canEditSelf && !isAdminMode} />
      <ManagerFeedbackModal isOpen={showManagerFeedbackModal} onClose={() => setShowManagerFeedbackModal(false)} ikr={ikr} onSubmit={(data) => onManagerFeedbackSubmit(ikr.id, data)} existingFeedback={evaluation?.managerFeedback} managers={managers} canEdit={canEditManager && !isAdminMode} evaluationMode={evaluationMode} viewer={viewer} />
    </>
  );
}
